import { Dispatch, FC, useCallback, useEffect, useState } from 'react';
import { CreateStudyAction, CreateStudyState } from '@/components/Studies/Create/Create.model';
import Button from '@/components/UI/Button';
import { _isEmpty } from '@/littledash';
import { useModalAction } from '@/utils/modal';
import {
  GroupTarget,
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionDetailType,
  SampleDefinitionFormData,
} from '@/model/SamplePlan.model';
import { useStudyCreateStep } from '@/utils/studyCreation/useStudyCreate';
import { FormActions } from '@/components/Studies/Create/Show.utils';
import { SamplePlanTableProvider } from '@/components/Samples/SamplePlanTable/SamplePlanTableProvider';
import { SamplePlanTable } from '@/components/Samples/SamplePlanTable/SamplePlanTable';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { TeamGlossary, TeamGlossaryApiId } from '@/model/Glosary.model';
import { isSubsetOfSets } from '@/utils/browser.utils';
import { MetadataGlossaryApiId, MetadataGlossaryV1 } from '@/model/Metadata.model';
import { RiAlertFill } from 'react-icons/ri';
import {
  useSamplePlanAnimalData,
  useSamplePlanGroupData,
} from '@/components/Studies/Create/Steps/SamplingPlan/SamplePlan.utils';

type SampleDefinitionIssue =
  | { type: 'invalid-sample-type' }
  | { type: 'invalid-sample-detail-type' }
  | { type: 'invalid-metadata' }
  | {
      type: 'invalid-target';
      target: { type: 'groups' } | { type: 'registered_animals' } | { type: 'planned_animals' };
    };

const samplePlanIssues = (
  sampleDefinitions: Array<SampleDefinitionFormData>,
  context: {
    metadataGlossary: Map<MetadataGlossaryApiId, MetadataGlossaryV1>;
    sampleGlossary: Map<TeamGlossaryApiId, TeamGlossary>;
    groupApiIds: Set<GroupTarget['api_id']>;
    plannedAnimalApiIds: Set<PlannedAnimalTarget['api_id']>;
    registeredAnimalApiIds: Set<RegisteredAnimalTarget['api_id']>;
  }
): Map<SampleDefinitionFormData['api_id'], Array<SampleDefinitionIssue>> => {
  const result = new Map<SampleDefinitionFormData['api_id'], Array<SampleDefinitionIssue>>();
  sampleDefinitions.forEach((sampleDefinition) => {
    if (context.sampleGlossary.has(sampleDefinition.sample_type)) {
      const sampleType = context.sampleGlossary.get(sampleDefinition.sample_type) as TeamGlossary;
      if (
        !isSubsetOfSets(
          new Set(sampleDefinition.details.map((detail) => detail.type)),
          new Set((sampleType.options?.details ?? []) as Array<SampleDefinitionDetailType>)
        )
      ) {
        result.set(sampleDefinition.api_id, [
          ...(result.get(sampleDefinition.api_id) ?? []),
          { type: 'invalid-sample-detail-type' },
        ]);
      }
    } else {
      result.set(sampleDefinition.api_id, [
        ...(result.get(sampleDefinition.api_id) ?? []),
        { type: 'invalid-sample-type' },
      ]);
    }

    switch (sampleDefinition.target.type) {
      case 'groups': {
        if (!isSubsetOfSets(new Set(sampleDefinition.target.targets), context.groupApiIds)) {
          result.set(sampleDefinition.api_id, [
            ...(result.get(sampleDefinition.api_id) ?? []),
            { type: 'invalid-target', target: { type: 'groups' } },
          ]);
        }
        break;
      }
      case 'planned_animals': {
        if (!isSubsetOfSets(new Set(sampleDefinition.target.targets), context.plannedAnimalApiIds)) {
          result.set(sampleDefinition.api_id, [
            ...(result.get(sampleDefinition.api_id) ?? []),
            { type: 'invalid-target', target: { type: 'planned_animals' } },
          ]);
        }
        break;
      }
      case 'registered_animals': {
        if (!isSubsetOfSets(new Set(sampleDefinition.target.targets), context.registeredAnimalApiIds)) {
          result.set(sampleDefinition.api_id, [
            ...(result.get(sampleDefinition.api_id) ?? []),
            { type: 'invalid-target', target: { type: 'registered_animals' } },
          ]);
        }
        break;
      }
    }
  });
  return result;
};

type SamplingPlanStepProps = {
  state: CreateStudyState;
  dispatch: Dispatch<CreateStudyAction>;
};
export const SamplingPlanStep: FC<SamplingPlanStepProps> = ({ state, dispatch }) => {
  const { openModal, closeModal } = useModalAction();
  const { setStepReady } = useStudyCreateStep();
  const [issues, updateIssues] = useState(new Map());

  const { response: sampleMetadataGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/metadata-glossary',
    query: {
      include_inactive: false,
      filter_type: 'sample_meta',
    },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-meta-load' } },
  });
  const { response: sampleGlossaryResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
      sort: 'title',
    },
    options: { onError: { slug: 'samples-glossary-load' } },
  });

  const groupData = useSamplePlanGroupData();
  const animals = useSamplePlanAnimalData();

  const disabled =
    _isEmpty(groupData.groupTargets) && _isEmpty(animals.planned_animals) && _isEmpty(animals.registered_animals);

  const onSubmit = useCallback((sample_definitions: Array<SampleDefinitionFormData>) => {
    dispatch({
      type: 'updateStudy',
      data: { ...state.study, sample_definitions },
    });
    closeModal();
    return Promise.resolve();
  }, []);

  useEffect(() => {
    if (sampleMetadataGlossaryResponse?.type === 'success' && sampleGlossaryResponse?.type === 'success') {
      const updatedIssues = samplePlanIssues(state.study.sample_definitions ?? [], {
        groupApiIds: new Set(groupData.groupIdMap.values()),
        registeredAnimalApiIds: new Set(animals.registered_animals.map((ra) => ra.api_id)),
        plannedAnimalApiIds: new Set(animals.planned_animals.map((pa) => pa.api_id)),
        sampleGlossary: new Map(
          ((sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []).map((gly) => [gly.api_id, gly])
        ),
        metadataGlossary: new Map(
          (sampleMetadataGlossaryResponse?.body ?? []).map((mgl) => [mgl.api_id as MetadataGlossaryApiId, mgl])
        ),
      });
      updateIssues(updatedIssues);
      setStepReady(updatedIssues.size === 0);
    } else {
      updateIssues((prevState) => (prevState.size === 0 ? prevState : new Map()));
      setStepReady((state.study.sample_definitions?.length ?? 0) === 0);
    }
  }, [
    setStepReady,
    updateIssues,
    state.study.sample_definitions,
    sampleGlossaryResponse,
    sampleMetadataGlossaryResponse,
    groupData.groupIdMap,
    animals.registered_animals,
    animals.planned_animals,
  ]);

  const openSamplePlanModal = () => {
    openModal('STUDY_CREATE_SAMPLE_PLAN', {
      sample_definitions: state.study.sample_definitions ?? [],
      groups: groupData.groupTargets,
      planned_animals: animals.planned_animals,
      registered_animals: animals.registered_animals,
      onSubmit,
    });
  };

  return (
    <>
      <div className="mb4" data-test-component="SamplingPlanStep" data-test-element="header">
        <h3 className="fw5 f5 pb2" data-testid="sampling-plan-title">
          Sampling plan
        </h3>
        <p data-testid="sampling-plan-description">Specify how samples should be collected from groups or animals.</p>
      </div>
      <div className="w-100 ui-card" data-test-component="SamplingPlanStep" data-test-element="card-container">
        {_isEmpty(state.study.sample_definitions) ? (
          <EmptyState disabled={disabled} onClick={openSamplePlanModal} />
        ) : (
          <>
            <div className="flex flex-row items-end justify-end ma3">
              <Button outline transparent testId="edit-sampling-plan-button" onClick={openSamplePlanModal}>
                {issues.size > 0 ? (
                  <span
                    className="flex flex-row items-center justify-center"
                    data-tooltip-id="global-tooltip-id"
                    data-tooltip-content={`Resolve ${issues.size} conflicts to proceed`}
                    data-test-element="edit-button-with-error"
                  >
                    <span className="mr1">Edit</span>
                    <RiAlertFill className="red" size={16} />
                  </span>
                ) : (
                  <span data-test-element="edit-button">Edit</span>
                )}
              </Button>
            </div>
            <SamplePlanTableProvider
              sample_definitions={state.study.sample_definitions ?? []}
              groups={groupData.groupTargets}
              registered_animals={animals.registered_animals}
              planned_animals={animals.planned_animals}
              metadata_glossary={sampleMetadataGlossaryResponse?.body ?? []}
              sample_glossary={(sampleGlossaryResponse?.body as { data?: Array<TeamGlossary> })?.data ?? []}
            >
              <SamplePlanTable />
            </SamplePlanTableProvider>
          </>
        )}
      </div>
      <FormActions state={state} dispatch={dispatch as () => void} />
    </>
  );
};

const EmptyState: FC<{ disabled: boolean; onClick: () => void }> = ({ disabled, onClick }) => (
  <div
    className="br2 bg-near-white tc flex items-center justify-center flex-column pa4 ma4"
    data-test-component="EmptyState"
    data-test-element="container"
  >
    <div className="mw6 center ph4 pa4">
      <h3 className="mb2" data-testid="empty-state-title">
        Add planned sample groups
      </h3>
      <p className="mb3" data-testid="empty-state-description">
        A preview of your treatment groups and any associated metadata will appear here
      </p>
      <Button
        stateless
        className="basier-reg blue ph0 mt2"
        disabled={disabled}
        testId="add-planned-samples-button"
        onClick={onClick}
      >
        <span
          data-tooltip-id={disabled ? 'global-tooltip-id' : undefined}
          data-tooltip-content={disabled ? 'Create at least one group before adding a sampling plan' : undefined}
        >
          Add planned samples
        </span>
      </Button>
    </div>
  </div>
);
